import type {
  CsatSurveyMethod,
  CsatSurveySendTime,
} from "@redotech/redo-support-settings-common/csat-survey";
import { Attachment } from "../../create-conversation-body";
import type {
  ConversationStatusV3,
  MessageChannelV3,
  TimeUnit,
} from "../rules-v3/types";
import { RuleActionType } from "./types";

export enum SetStatusActionStatus {
  OPEN = "open",
  CLOSED = "closed",
  ARCHIVED = "archived",
  SPAM = "spam",
  SNOOZED = "snoozed",
}

export enum SetStatusSnoozePeriod {
  TODAY = "today",
  TOMORROW = "tomorrow",
  WEEKEND = "weekend",
  NEXT_WEEK = "nextWeek",
}

/**
 * Snooze durations for the V3 (support v2) set-status action. Values mirror
 * the CRDB `support_ticket_snooze_duration` enum and the snooze-macro options
 * (`SupportTicketSnoozeOptions` minus `"custom"`) so a snoozed rule wakes the
 * ticket using the same relative-offset semantics as a manual snooze macro.
 *
 * This intentionally replaces the v1-style `SetStatusSnoozePeriod` day-boundary
 * periods (today/tomorrow/weekend/nextWeek) for v3 — the legacy `SET_STATUS`
 * action still uses `SetStatusSnoozePeriod`.
 */
export enum SetStatusSnoozeDuration {
  ONE_HOUR = "1h",
  FOUR_HOURS = "4h",
  TWENTY_FOUR_HOURS = "24h",
}

export interface SetStatusActionConfig {
  actionType: RuleActionType.SET_STATUS;
  status: SetStatusActionStatus;
  snoozePeriod?: SetStatusSnoozePeriod;
}

export interface AddTagActionConfig {
  actionType: RuleActionType.ADD_TAG;
  tag: string[];
}

export interface RemoveTagActionConfig {
  actionType: RuleActionType.REMOVE_TAG;
  tag: string[];
}

export interface AutoReplyActionConfig {
  actionType: RuleActionType.AUTO_REPLY;
  textContent: string;
  htmlContent: string;
  attachments?: Attachment[];
}

export const ASSIGN_ACTION_UNASSIGN_USER_ID = "__unassign__";
export const ASSIGN_ACTION_UNASSIGN_USER_NAME = "Unassign";

export interface AssignActionConfig {
  actionType: RuleActionType.ASSIGN;
  user: { id: string; name: string };
}

export enum AssignV3Strategy {
  ROUND_ROBIN = "round_robin",
  BALANCED = "balanced",
}

/**
 * Subset of `ConversationAssigneeType` that the rule engine can resolve from a
 * trigger actor today. `CHAT_FLOW` is intentionally excluded — no current
 * trigger produces a chat-flow actor, so allowing it would ship unreachable
 * config.
 */
export type AssignV3ActorAssigneeType = "USER" | "AI";

export type AssignV3ActionMode =
  | { _tag: "specific_users"; mongoUserIds: string[] }
  | { _tag: "user_group"; userGroupId: string }
  | { _tag: "unassign" }
  | { _tag: "actor"; assigneeTypes: AssignV3ActorAssigneeType[] }
  | { _tag: "ai" };

export interface AssignV3ActionConfig {
  actionType: RuleActionType.ASSIGN_V3;
  mode: AssignV3ActionMode;
  strategy: AssignV3Strategy;
  /**
   * Optional per-user open-ticket capacity. When set, users at or above
   * this many open tickets are excluded from the pool; if every candidate
   * is at capacity, the rule returns `skipped`. `null` / omitted = no cap.
   */
  maxTicketsPerUser?: number | null;
}

/**
 * Finds the oldest open unassigned ticket for the account and assigns it
 * using the same pool-selection logic as `AssignV3ActionConfig`. The rule's
 * triggering ticket is not modified; only the oldest unassigned ticket is.
 */
export interface AssignOldestTicketActionConfig {
  actionType: RuleActionType.ASSIGN_OLDEST_TICKET;
  mode: AssignV3ActionMode;
  strategy: AssignV3Strategy;
  maxTicketsPerUser?: number | null;
}

export interface LikeCommentActionConfig {
  actionType: RuleActionType.LIKE_COMMENT;
}

export interface HideCommentActionConfig {
  actionType: RuleActionType.HIDE_COMMENT;
}

/**
 * Conversation priority is currently a `"URGENT" | null` field on the
 * conversation row. The action can either set it to `URGENT` or clear it
 * (`null`). If more priority levels are added later, extend the union.
 */
export type RuleActionPriority = "URGENT" | null;

export interface SetPriorityActionConfig {
  actionType: RuleActionType.SET_PRIORITY;
  priority: RuleActionPriority;
}

/**
 * V3 set-status action. Uses CRDB `ConversationStatus` values directly
 * instead of the legacy V2 `SetStatusActionStatus` enum. Supports all
 * statuses the CRDB conversation table allows.
 *
 * For SNOOZED, `snoozeDuration` determines the wake-time as a relative offset
 * (1h/4h/24h) from when the rule runs. The unsnooze workflow is started
 * automatically by `ConversationService`.
 */
export type SetStatusV3Status =
  | "OPEN"
  | "CLOSED"
  | "SNOOZED"
  | "IN_PROGRESS"
  | "ARCHIVED"
  | "DELETED";

export interface SetStatusV3ActionConfig {
  actionType: RuleActionType.SET_STATUS_V3;
  status: SetStatusV3Status;
  snoozeDuration?: SetStatusSnoozeDuration;
}

export interface StartAiAgentExecutionActionConfig {
  actionType: RuleActionType.START_AI_AGENT_EXECUTION;
}

export interface HandoffAiActionConfig {
  actionType: RuleActionType.HANDOFF_AI;
}

/**
 * Backend-only action that triggers the `generateConversationInsightsV2Workflow`
 * Temporal workflow for the rule's conversation. Not exposed in the merchant-app
 * rule editor — added to rules via seeded data or internal tooling.
 */
export interface GenerateConversationInsightsActionConfig {
  actionType: RuleActionType.GENERATE_CONVERSATION_INSIGHTS;
}

export interface SendCsatSurveyActionConfig {
  actionType: RuleActionType.SEND_CSAT_SURVEY;
  surveySendTime?: CsatSurveySendTime;
  surveyMethods?: CsatSurveyMethod[];
  allowRemarks?: boolean;
  headerImage?: string;
  surveyMessage?: string;
}

/**
 * Action that merges all OTHER tickets owned by the triggering ticket's
 * customer into the triggering ticket. The triggering ticket is always the
 * destination; matching peers close as DUPLICATE.
 *
 * Both fields are required — there is no "merge everything ever" default,
 * since that would let an old archived ticket re-open as a side effect.
 */
export interface MergeSupportTicketActionConfig {
  actionType: RuleActionType.MERGE_SUPPORT_TICKET;
  /**
   * Only peer tickets currently in one of these statuses are eligible to
   * merge in. Empty array = nothing eligible (no-op).
   */
  allowedStatuses: ConversationStatusV3[];
  /**
   * Only peer tickets whose most recent message (any sender) is within this
   * window are eligible. Tickets with no messages at all are ineligible.
   */
  maxAgeSinceLastMessage: { value: number; unit: TimeUnit };
}

export interface SplitSupportTicketActionConfig {
  actionType: RuleActionType.SPLIT_SUPPORT_TICKET;
  routeFutureMessagesToNewTicket: boolean;
}

export interface StopTopicContinuationActionConfig {
  actionType: RuleActionType.STOP_TOPIC_CONTINUATION;
  /**
   * Channel types to stop continuation for. When undefined (legacy/default),
   * all continuation-enabled lanes are archived. When provided (even empty),
   * only the specified channels are archived; an empty array is a no-op.
   */
  channels?: MessageChannelV3[];
}

export type RuleActionConfig =
  | SetStatusActionConfig
  | AddTagActionConfig
  | RemoveTagActionConfig
  | AutoReplyActionConfig
  | AssignActionConfig
  | AssignV3ActionConfig
  | AssignOldestTicketActionConfig
  | LikeCommentActionConfig
  | HideCommentActionConfig
  | SetPriorityActionConfig
  | SetStatusV3ActionConfig
  | StartAiAgentExecutionActionConfig
  | HandoffAiActionConfig
  | GenerateConversationInsightsActionConfig
  | SendCsatSurveyActionConfig
  | MergeSupportTicketActionConfig
  | SplitSupportTicketActionConfig
  | StopTopicContinuationActionConfig;
